import { type UseQueryOptions, useQuery } from '@tanstack/react-query';

interface IncidentUpdate {
  body: string;
  created_at: string;
  display_at: string;
  id: string;
  incident_id: string;
  status: string;
  updated_at: string;
}

interface Component {
  id: string;
  name: string;
  status: string;
}

interface Incident {
  created_at: string;
  id: string;
  impact: 'critical' | 'major' | 'minor' | 'none';
  incident_updates: IncidentUpdate[];
  monitoring_at: string | null;
  name: string;
  page_id: string;
  resolved_at: string | null;
  shortlink: string;
  components: Component[];
  status: 'investigating' | 'identified' | 'monitoring' | 'resolved' | 'postmortem';
  updated_at: string;
}

interface StatusPageResponse {
  page: {
    id: string;
    name: string;
    url: string;
    updated_at: string;
  };
  incidents: Incident[];
}

const QUERY_KEY = 'UNRESOLVED_INCIDENTS';

//https://www.coblistatus.com/api/v2#javascript-library
const fetchUnresolvedIncidents = async (): Promise<StatusPageResponse> => {
  const response = await fetch('https://www.coblistatus.com/api/v2/incidents/unresolved.json');

  if (!response.ok) {
    throw new Error(`Failed to fetch unresolved incidents: ${response.statusText}`);
  }

  return response.json();
};

const useUnresolvedIncidents = (options: Partial<UseQueryOptions<StatusPageResponse>> = {}) => {
  return useQuery<StatusPageResponse>({
    queryKey: [QUERY_KEY],
    queryFn: fetchUnresolvedIncidents,
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    staleTime: 2 * 60 * 1000, // Consider data stale after 2 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    ...options,
  });
};

export { useUnresolvedIncidents };
export type { Incident, StatusPageResponse };
